import {
  AfterViewInit,
  Component,
  DestroyRef,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  ViewChild,
} from '@angular/core';
import { DOCUMENT, NgClass, AsyncPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  combineLatest,
  concatMap,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ToastrService } from 'ngx-toastr';

import {
  AttributeService,
  CartService,
  ConfigService,
  EventsService,
  GoogleAnalyticsService,
  Product,
  ProductInventory,
  ProductionService,
  ProductMediaGallery,
  ProductService,
  generateSliderConfig,
  ProductStockService,
  WishListApiService,
  CustomerService,
  CategoryService,
  ProductType,
  Cart,
  BundledOptionGroup,
  getExistedBlocksLength,
  NgxGalleryComponent,
  NgxGalleryImage,
  NgxGalleryOptions,
  TranslateDirective,
  TranslatePipe,
} from '@speedy/core';

import { ReviewStatus } from './interfaces/Review';
import { ConfigurableComponent } from './components/configurable/configurable.component';
import { VideoService } from './services/video.service';
import { ReviewsService } from './services/reviews.service';
import { BundleOptionsComponent } from './components/bundle-options/bundle-options.component';
import { galleryOptions } from './contants/gallery-options';
import { BlocksGridComponent } from '../../../shared/components/blocks-grid/blocks-grid.component';
import { ProductsSliderComponent } from '../../../shared/components/products-slider/products-slider.component';
import { AddReviewFormComponent } from './components/reviews-form/reviews-form.component';
import { ReviewsListComponent } from './components/reviews-list/reviews-list.component';
import { ProductDescriptionComponent } from './components/product-description/product-description.component';
import { SpeedyButtonComponent } from '../../../shared/components/speedy-button/speedy-button.component';
import { SpeedyQtyComponent } from '../../../shared/components/speedy-qty/speedy-qty.component';
import { ProductStockOptionsComponent } from '../../../shared/components/product-stock-options/product-stock-options.component';
import { LeasingCalculatorComponent } from './components/leasing-calculator/leasing-calculator.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { PriceComponent } from '../../../shared/components/price/price.component';
import { CurrencyComponent } from '../../../shared/components/currency/currency.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss'],
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    NgxGalleryComponent,
    MatIcon,
    MatProgressSpinner,
    TranslateDirective,
    CurrencyComponent,
    PriceComponent,
    NgClass,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    LeasingCalculatorComponent,
    ConfigurableComponent,
    BundleOptionsComponent,
    ProductStockOptionsComponent,
    SpeedyQtyComponent,
    SpeedyButtonComponent,
    ProductDescriptionComponent,
    ReviewsListComponent,
    AddReviewFormComponent,
    ProductsSliderComponent,
    BlocksGridComponent,
    AsyncPipe,
    TranslatePipe,
    TranslateDirective,
  ],
})
export class ProductComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(NgxGalleryComponent) gallery!: NgxGalleryComponent;
  @ViewChild('configurableComponent') configurableComponent!: ConfigurableComponent;
  @ViewChild('bundleComponent') bundleComponent!: BundleOptionsComponent;

  private readonly cartService = inject(CartService);
  private readonly route = inject(ActivatedRoute);
  private readonly configService = inject(ConfigService);
  private readonly productionService = inject(ProductionService);
  private readonly productService = inject(ProductService);
  private readonly eventsService = inject(EventsService);
  private readonly videoService = inject(VideoService);
  private readonly attributeService = inject(AttributeService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly reviewsService = inject(ReviewsService);
  public readonly productStockService = inject(ProductStockService);
  private readonly wishListApiService = inject(WishListApiService);
  private readonly customerService = inject(CustomerService);
  private readonly alertService = inject(ToastrService);
  private readonly categoryService = inject(CategoryService);
  private readonly injector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);

  private readonly destroyRef = inject(DestroyRef);

  reviewAdded$ = new Subject<void>();
  childProduct$ = new Subject<Product>();

  readonly blockIdentifiers = [
    'product_grid_block_1',
    'product_grid_block_2',
    'product_grid_block_3',
    'product_grid_block_4',
  ];

  product$ = combineLatest([
    this.route.data,
    this.childProduct$.pipe(startWith(null)),
  ]).pipe(
    map(([data, childProduct]) => {
      const product = childProduct ?? data?.product;

      if (!product) return {} as Product;

      console.log(product);
      this.initProductSchema(product);
      this.initImageGallery(product);
      this.initProductMediaDetails(product);
      this.checkIsLeaseCalculatorVisible(product);
      this.initErplyProductFiles(product);
      this.triggerGoogleEvents(product);
      return product as Product;
    }),
    shareReplay(1),
    takeUntilDestroyed(this.destroyRef),
  );
  relatedProducts$ = this.product$.pipe(
    concatMap((product) => {
      if (product) {
        return this.productService.getRelatedProducts(product).pipe(
          take(1),
          tap((products) => {
            this.relatedProductsEvent(product, products);
          }),
        );
      }
      return of([]);
    }),
    takeUntilDestroyed(this.destroyRef),
  );
  upSellProducts$ = this.product$.pipe(
    concatMap((product) => {
      if (product?.upsells) {
        const upSells = JSON.parse(product.upsells);
        if (upSells?.length) {
          return this.productService.getProductsByIdBatch(upSells).pipe(take(1));
        }
      }
      return of([]);
    }),
    takeUntilDestroyed(this.destroyRef),
  );
  crossSellProducts$ = this.product$.pipe(
    concatMap((product) => {
      if (product?.crollsells) {
        const crossSell = JSON.parse(product.crollsells);
        if (crossSell?.length) {
          return this.productService.getProductsByIdBatch(crossSell).pipe(take(1));
        }
      }
      return of([]);
    }),
    takeUntilDestroyed(this.destroyRef),
  );
  recentlyViewedProducts$ = this.product$.pipe(
    concatMap((product) => {
      if (product) {
        return this.productService
          .getRecentlyViewedProducts(product, this.productsRecentlyViewedQty)
          .pipe(take(1));
      }
      return of([]);
    }),
    takeUntilDestroyed(this.destroyRef),
  );

  reviews$ = combineLatest([this.product$, this.reviewAdded$.pipe(startWith(null))]).pipe(
    switchMap(([product]) => {
      if (product) {
        return this.reviewsService
          .getReviews(product.sku)
          .pipe(
            map((reviews) =>
              reviews.filter((review) => review.review_status === ReviewStatus.Approved),
            ),
          );
      }
      return of([]);
    }),
  );

  productBlockLength$ = this.product$.pipe(
    concatMap(() =>
      runInInjectionContext(this.injector, () =>
        getExistedBlocksLength(this.blockIdentifiers),
      ),
    ),
  );

  loading = false;
  wishListLoading = false;
  loadingSuccess = false;
  productSchema = {};
  qty = 1;
  files: { url: string; name: string }[] = [];
  product_href = '';
  placeholder = this.configService.getPlaceholder();
  mediaDetails: ProductMediaGallery[] = [];
  productPriceBundled = 0;
  ProductType = ProductType;

  productInventoryList: ProductInventory[] = [];
  calculatorOpenState = false;
  isCalculatorVisible = false;
  productTableRowsCount = 22;

  showLease = false;
  leaseInterest = 10.9;
  leaseContract = 35.9;
  leaseMonthlyFee = 2.99;
  leasePeriods = [6, 12, 18, 24, 30, 36, 42, 48, 53, 60];
  leasePayments = [0, 10, 20, 30, 40, 50, 60, 70];
  leaseFreePeriod = 6;
  leasePeriod = this.leasePeriods[1];
  leasePayment = this.leasePayments[0];
  monthlyPayment = NaN;
  leaseStarting = NaN;

  isLhvVisible = false;
  isInbankVisible = false;
  isEstoVisible = false;
  isTFbankVisible = false;

  galleryImages!: NgxGalleryImage[];
  galleryOptions: NgxGalleryOptions[] = galleryOptions;

  productsRecentlyViewedQty = 7;
  productsRecentlyViewedConfig = generateSliderConfig('recently-viewed');
  relatedProductsConfig = generateSliderConfig('related-products');
  upSellProductsConfig = generateSliderConfig('upsell-products');
  crossSellProductsConfig = generateSliderConfig('cross-sell-products');

  ngOnInit() {
    if (this.isBrowserMode()) {
      this.product_href = window.location.href;
    }
  }

  ngAfterViewInit() {
    this.onGalleryBack();
  }

  ngOnDestroy(): void {
    if (this.productionService.isServerMode()) return;

    this.categoryService.removeLastCategory();
  }

  initErplyProductFiles(product: Product) {
    if (product?.erply_files) {
      this.files = JSON.parse(product.erply_files);
    } else {
      this.files = [];
    }
  }

  initProductSchema(product: Product) {
    this.productSchema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      image: [product.image],
      description: product.short_description,
      sku: product.sku,
      brand: {
        '@type': 'Brand',
        name: product.option_text_brand,
      },
      offers: {
        '@type': 'Offer',
        url: this.document.location.href,
        priceCurrency: 'EUR',
        price: product.finalPrice,
        availability: product?.stock?.is_in_stock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      },
    };
  }

  initImageGallery(product: Product, childProduct?: Product) {
    this.galleryImages = [];

    if (childProduct) {
      this.addChildProductImageToGallery(childProduct);
    }

    const mediaGallery = Array.isArray(product.media_gallery)
      ? product.media_gallery
      : [product.media_gallery];

    mediaGallery.forEach((image: ProductMediaGallery) => {
      if (
        parseInt(image?.disabled as string) === 1 ||
        this.isProductDetailsMediaItem(product, image)
      ) {
        return;
      }

      const imageUrl = image?.file;
      this.addGalleryImage(imageUrl);
    });

    if (
      mediaGallery.length === 0 ||
      mediaGallery[0] === undefined ||
      this.galleryImages.length === 0
    ) {
      const imageUrl = mediaGallery[0]?.file || product?.image;
      this.addGalleryImage(imageUrl);
    }
  }

  initProductMediaDetails(product: Product) {
    this.mediaDetails = [];
    if (product.media_gallery && product.media_gallery.length) {
      product.media_gallery.map((mediaItem) => {
        if (this.isProductDetailsMediaItem(product, mediaItem)) {
          this.mediaDetails.push(mediaItem);
        }
      });

      this.videoService.initMediaDetailsVideoEmbededUrls(this.mediaDetails);
      this.videoService.initMediaDetailsVideoResources(this.mediaDetails);
    }
  }

  addChildProductImageToGallery(childProduct: Product) {
    const imageUrl = childProduct.image;
    if (!imageUrl) return;
    this.addGalleryImage(imageUrl);
  }

  addGalleryImage(imageUrl: string) {
    if (!imageUrl) {
      return;
    }
    const placeholderImage = {
      small: this.placeholder,
      medium: this.placeholder,
      big: this.placeholder,
    };
    if (imageUrl && (imageUrl.includes('placeholder') || imageUrl === 'no_selection')) {
      this.galleryImages.push(placeholderImage);
    } else {
      const getImageUrl = () =>
        imageUrl && imageUrl.includes(this.configService.getProductMediaUrl('image'))
          ? imageUrl
          : this.configService.getProductMediaUrl('image') + imageUrl;

      const mediumUrl = getImageUrl();
      const bigUrl =
        imageUrl && imageUrl.includes(this.configService.mediaUrl)
          ? imageUrl
          : this.configService.mediaUrl + 'catalog/product/' + imageUrl;

      this.galleryImages.push({
        small: mediumUrl,
        medium: mediumUrl,
        big: bigUrl,
      });
    }
  }

  addToCart(product: Product) {
    if (!this.validate(product)) return;

    this.loading = true;
    let request$: Observable<Cart>;

    switch (product.type_id) {
      case ProductType.Configurable:
        request$ = this.cartService.addToCartConfigurable(
          product,
          this.qty,
          this.configurableComponent.getAddToCartOptions(),
        );
        break;
      case ProductType.Bundle:
        request$ = this.cartService.addToCartBundle(
          product,
          this.qty,
          this.bundleComponent.getBundleOptions(),
        );
        break;
      default:
        request$ = this.cartService.addToCart(product, this.qty);
        break;
    }

    request$.pipe(take(1)).subscribe({
      next: () => {
        this.loading = false;
        this.qty = 1;
      },
      error: (error) => {
        this.loading = false;
        this.alertService.error(error.error?.message ?? error.error);
      },
    });
  }

  changeQty(qty: number, product: Product) {
    this.qty = qty;

    switch (product.type_id) {
      case ProductType.Configurable:
        this.productService.considerTierPrices(
          product,
          this.configurableComponent.selectedChildProduct,
          this.qty,
        );
        break;
      case ProductType.Bundle:
        break;
      default:
        this.productService.considerTierPrices(product, product, this.qty);
        break;
    }
  }

  /**
   * Checking selected option values to count the prices of it with qty and create a sum of them
   * @param options - The array with selected options values
   */
  bundledOptionsChanged(options: BundledOptionGroup[]): void {
    const price: number[] = [];

    options.forEach((option) => {
      const products = option.option_selections.map((selectionId) =>
        option.product_links.find(({ id }) => id === selectionId),
      );

      if (products && products.length > 0 && !!products[0]) {
        price.push(
          option.option_qty *
            products.reduce((p, c) => p + (c?.regular_price as number), 0),
        );
      }
    });

    const priceSum = price.reduce((p, c) => p + c, 0);
    this.productPriceBundled = priceSum;
  }

  /**
   * Checks the visibility of different lease calculators based on the final price of a product.
   * @param product - The product object containing the final price.
   */
  checkIsLeaseCalculatorVisible(product: Product) {
    const minTFbank = this.configService.getMinOrderTotal('tfbank_hp');
    const maxTFbank = this.configService.getMaxOrderTotal('tfbank_hp');
    const minLhv = this.configService.getMinOrderTotal('lhv_hp');
    const maxLhv = this.configService.getMaxOrderTotal('lhv_hp');
    const minEsto = this.configService.getMinOrderTotal('esto_hirepurchase');
    const maxEsto = this.configService.getMaxOrderTotal('esto_hirepurchase');
    const minInbank = this.configService.getMinOrderTotal('inbank');
    const maxInbank = this.configService.getMaxOrderTotal('inbank');

    const finalPrice = Number.parseInt(product.finalPrice);

    this.isTFbankVisible =
      finalPrice >= Number.parseInt(minTFbank) &&
      finalPrice <= Number.parseInt(maxTFbank);
    this.isLhvVisible =
      finalPrice >= Number.parseInt(minLhv) && finalPrice <= Number.parseInt(maxLhv);
    this.isEstoVisible =
      finalPrice >= Number.parseInt(minEsto) && finalPrice <= Number.parseInt(maxEsto);
    this.isInbankVisible =
      finalPrice >= Number.parseInt(minInbank) &&
      finalPrice <= Number.parseInt(maxInbank);
    this.isCalculatorVisible =
      finalPrice >= Number.parseInt(minEsto) && finalPrice <= Number.parseInt(maxLhv);
  }

  /** GOOGLE ANALITYCS | GOOGLE TAG MANAGER */
  relatedProductsEvent(product: Product, relatedProducts: Product[]) {
    if (relatedProducts?.length) {
      this.googleAnalyticsService.productRelatedProducts(product, relatedProducts);
    }
  }

  /** GOOGLE ANALITYCS | GOOGLE TAG MANAGER */
  triggerGoogleEvents(product: Product) {
    this.eventsService.broadcast('product_view', product);
    this.googleAnalyticsService.productDetailsView(product);
  }

  onGalleryBack() {
    if (this.productionService.isServerMode()) return;
    const galleryThumbnails = document.querySelectorAll('a.ngx-gallery-thumbnail');
    galleryThumbnails.forEach((item) => {
      item.addEventListener('click', () => {
        window.history.pushState(null, 'any', this.product_href);
        window.onpopstate = () => {
          this.gallery.preview.close();
        };
      });
    });
  }

  validate(product: Product) {
    switch (product.type_id) {
      case ProductType.Configurable:
        return this.configurableComponent.validate();
      case ProductType.Bundle:
        return this.bundleComponent.validate();
      default:
        return true;
    }
  }

  getAttributeText(product: Product, attrCode: string) {
    return this.attributeService.getAttributeText(attrCode, product[attrCode]);
  }

  getAttributeLabel(attrCode: string) {
    return this.attributeService.getAttributeLabel(attrCode);
  }

  optionChanged(product: Product, childProduct: Product) {
    const excludeFields = [
      'description',
      'finalPrice',
      'media_gallery',
      'regularPrice',
      'specification',
    ];

    const updatedProduct = this.updateObjectExcept(product, childProduct, excludeFields);

    this.childProduct$.next(updatedProduct);
  }

  updateObjectExcept<T>(object1: T, object2: T, excludeFields: string[]) {
    // Create a copy of object1
    const newObject = { ...object1 };

    // Loop through all keys of object2
    for (const key in object1) {
      // Check if the key is not in the excludeFields array
      if (excludeFields.includes(key)) {
        // Update the newObject with the value from object2 if it exists, otherwise set it to null
        newObject[key] = key in (object2 as object) ? object2[key] : null;
      }
    }

    // Return the new object
    return newObject;
  }

  isProductDetailsMediaItem(product: Product, mediaItem: ProductMediaGallery) {
    return (
      product.product_details_image_1 === mediaItem?.file ||
      product.product_details_image_2 === mediaItem?.file
    );
  }

  addProductToWishList(productId: number): void {
    if (this.customerService.isLoggedIn()) {
      this.addToWishlist(productId);
    } else {
      this.wishListApiService.notifyUserToLogIn();
      this.wishListApiService.saveItemToWishList(productId);
    }
  }

  isBrowserMode() {
    return this.productionService.isBrowserMode();
  }

  private addToWishlist(productId: number): void {
    this.wishListApiService
      .addToWishList(productId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.success('Product was added to wishlist');
          this.loading = false;
        },
        error: (error) => {
          this.alertService.error(error);
          this.loading = false;
        },
      });
  }
}
